import type {ImageItemObject} from '@PosterWhiteboard/items/image-item/image-item.class';
import {ImageItem} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {CopyableItemStyles, InitItemOpts} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {Page} from '@PosterWhiteboard/page/page.class';
import {FilterOrders, ItemEffects} from '@PosterWhiteboard/classes/item-effects.class';
import {ImageItemSource, ScreenTier} from '@Libraries/image-item.library';
import {hasTransparency} from '@Utils/image.util';
import {GROWL_TYPE} from '@Libraries/message-growl.types';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import {BackgroundTypeName} from '@PosterWhiteboard/page/background/background.types';
import {buildColorBackgroundItemObject} from '@PosterWhiteboard/page/background/background-color.util';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {DEFAULT_BACKGROUND_COLOR} from '@PosterWhiteboard/page/page.types';
import {hexToRgba} from '@Utils/color.util';

const TRANSPARENCY_DISABLED = 'disabled';

export const transparencyStates = {
  DISABLED: TRANSPARENCY_DISABLED,
  ON: 'on',
  OFF: 'off',
};

export interface ImageBackgroundObject extends ImageItemObject {
  type: BackgroundTypeName.IMAGE;
  transparency: string;
  attributionName?: string;
  attributionURL?: string;
  licenseName?: string;
  licenseURL?: string;
  displayAttribution: boolean;
  idUser: number | null;
}

export class ImageBackground extends ImageItem {
  public override gitype = ITEM_TYPE.IMAGEBACKGROUND;
  public type: BackgroundTypeName.IMAGE = BackgroundTypeName.IMAGE;

  public transparency = TRANSPARENCY_DISABLED;
  public attributionName?: string;
  public attributionURL?: string;
  public licenseName?: string;
  public licenseURL?: string;
  public displayAttribution = true;
  public idUser: number | null = null;

  public constructor(page: Page) {
    super(page);
    this.isBackground = true;
    this.effects = new ItemEffects(this, FilterOrders.IMAGEBACKGROUND);
  }

  public override toObject(): ImageBackgroundObject {
    return {
      ...super.toObject(),
      type: this.type,
      transparency: this.transparency,
      attributionName: this.attributionName,
      attributionURL: this.attributionURL,
      licenseName: this.licenseName,
      licenseURL: this.licenseURL,
      displayAttribution: this.displayAttribution,
      idUser: this.idUser,
    };
  }

  protected override getScreenUrl(): string {
    if (this.hashedFilename && this.fileExtension) {
      return this.getUrlForImageItemScreenTier(ScreenTier.BIG);
    }
    if (this.uploadingImageData?.dataUrl) {
      return this.uploadingImageData.dataUrl;
    }
    throw new Error(`Neither hashedFilename+Extension nor dataUrl is present`);
  }

  public override async updateFabricObject(): Promise<void> {
    await super.updateFabricObject();
    this.scaleFabricObjectToFillPage();
  }

  public override onRemove(): void {
    this.isUploadPollingInitialized = false;
    super.onRemove();
  }

  protected override onItemDoubleClicked(): void {
    getPosterUi().onOpenImageBackgroundCropModal();
  }

  protected override onItemInitialized(): Promise<void> {
    return new Promise((resolve) => {
      this.hasTransparency = this.hashedFilename && this.isJPG() ? false : hasTransparency(this.imageElement);
      this.verifyTransparencyFlags();
      resolve();
    });
  }

  public override getCopyableStyles(): CopyableItemStyles {
    return {
      ...super.getCopyableStyles(),
    };
  }

  public override async pasteStyles(copiedStyles: CopyableItemStyles): Promise<void> {
    await super.pasteStyles(copiedStyles);
  }

  public override async initFabricObject(opts: InitItemOpts = {}): Promise<void> {
    await super.initFabricObject(opts);
    this.hasTransparency = this.hashedFilename && this.isJPG() ? false : hasTransparency(this.imageElement);
    this.verifyTransparencyFlags();
  }

  protected override async onItemUpdatedFromObject(): Promise<void> {
    await super.onItemUpdatedFromObject();
    // Init applies the saved object twice (copyVals + a trailing updateFromObject); re-derive here
    // so that trailing pass can't re-stamp a stale "disabled" over the state set during fabric init.
    this.verifyTransparencyFlags();
  }

  protected verifyTransparencyFlags(): void {
    if (this.hasTransparency === false && !this.isBackgroundTransparencyDisabled()) {
      this.transparency = transparencyStates.DISABLED;
    } else if (this.hasTransparency && this.isBackgroundTransparencyDisabled()) {
      this.transparency = transparencyStates.ON;
    }
  }

  public isBackgroundImageTransparencyOn(): boolean {
    return this.transparency === transparencyStates.ON;
  }

  public isBackgroundTransparencyDisabled(): boolean {
    return this.transparency === transparencyStates.DISABLED;
  }

  public requiresAttribution(): boolean {
    return !!this.attributionName && !!this.licenseName && this.imageSource !== ImageItemSource.GETTY && this.imageSource !== ImageItemSource.STORYBLOCKS;
  }

  public override onUploadingImageFailed(e: Error): void {
    console.error(e);
    getPosterUi().showMessageGrowl({
      text: window.i18next.t('pmwjs_image_upload_error'),
      type: GROWL_TYPE.DANGER,
    });
    void this.page.items.replaceActiveBackground(buildColorBackgroundItemObject(this.page, FillTypes.SOLID, [hexToRgba(DEFAULT_BACKGROUND_COLOR)]));
  }
}
